import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { getStatusColor } from "../core/btw-status-colors";
import { lockBodyScroll, unlockBodyScroll } from "../core/scroll-lock";
import { getLabelColors } from "./btw-label-pill";

const QUAL_OPTIONS = [
  { value: "", label: "Alle" },
  { value: "qualified", label: "Geschikt" },
  { value: "disqualified", label: "Afgewezen" },
  { value: "incomplete", label: "Incompleet" },
];

const Section = ({ title, children }) => (
  <div className="border-b border-gray-100 px-5 py-4 last:border-b-0">
    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
      {title}
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const Option = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm transition ${
      active
        ? "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200"
        : "text-gray-700 hover:bg-gray-50"
    }`}
  >
    <span className="flex items-center gap-2">{children}</span>
    {active ? (
      <svg className="h-3.5 w-3.5 text-emerald-600" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
          clipRule="evenodd"
        />
      </svg>
    ) : null}
  </button>
);

const Toggle = ({ checked, onChange, label, description }) => (
  <label className="flex cursor-pointer items-start gap-3 rounded-lg px-2.5 py-2 hover:bg-gray-50">
    <span
      className={`relative mt-0.5 inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition ${
        checked ? "bg-emerald-500" : "bg-gray-300"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </span>
    <span className="flex-1">
      <span className="block text-sm font-medium text-gray-900">{label}</span>
      {description ? (
        <span className="block text-xs text-gray-500">{description}</span>
      ) : null}
    </span>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="sr-only"
    />
  </label>
);

const BtwFiltersPanel = ({
  isOpen,
  onClose,
  filters,
  setFilter,
  resetFilters,
  statuses = [],
  admins = [],
  labels = [],
  currentUserId,
  showStatusFilter = true,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    lockBodyScroll();
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", esc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const setAssignee = (mode, value) => {
    setFilter("mine", mode === "mine");
    setFilter("unassigned", mode === "unassigned");
    setFilter("assignedToAdminId", mode === "admin" ? value : "");
  };

  const assigneeMode = filters.mine
    ? "mine"
    : filters.unassigned
    ? "unassigned"
    : filters.assignedToAdminId
    ? "admin"
    : "all";

  return ReactDOM.createPortal(
    <div className="fixed inset-0" style={{ zIndex: 9999 }}>
      <div
        onClick={onClose}
        className="absolute inset-0 bg-gray-900/30 transition-opacity"
      />
      <aside
        className="absolute right-0 top-0 flex h-full w-[380px] flex-col bg-white shadow-2xl"
        style={{
          animation: "slideInRight 200ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <header className="flex flex-shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Filters</h3>
            <p className="text-xs text-gray-500">Wijzigingen worden direct toegepast</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Sluiten"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          {showStatusFilter ? (
            <Section title="Workflow-status">
              <Option
                active={!filters.workflowStatusId}
                onClick={() => setFilter("workflowStatusId", "")}
              >
                <span className="text-gray-700">Alle statussen</span>
              </Option>
              {statuses.map((s) => {
                const c = getStatusColor(s.color);
                return (
                  <Option
                    key={s._id}
                    active={filters.workflowStatusId === s._id}
                    onClick={() => setFilter("workflowStatusId", s._id)}
                  >
                    <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                    <span>{s.name}</span>
                    {s.isTerminal ? (
                      <span className="text-[10px] uppercase tracking-wide text-gray-400">
                        eind
                      </span>
                    ) : null}
                  </Option>
                );
              })}
            </Section>
          ) : null}

          <Section title="Toegewezen aan">
            <Option active={assigneeMode === "all"} onClick={() => setAssignee("all")}>
              <span className="text-gray-700">Iedereen</span>
            </Option>
            {currentUserId ? (
              <Option active={assigneeMode === "mine"} onClick={() => setAssignee("mine")}>
                <span className="text-gray-700">Toegewezen aan mij</span>
              </Option>
            ) : null}
            <Option
              active={assigneeMode === "unassigned"}
              onClick={() => setAssignee("unassigned")}
            >
              <span className="text-gray-700">Niet toegewezen</span>
            </Option>
            {admins.length > 0 ? (
              <div className="my-1 border-t border-gray-100" />
            ) : null}
            {admins.map((a) => (
              <Option
                key={a._id}
                active={
                  assigneeMode === "admin" && filters.assignedToAdminId === a._id
                }
                onClick={() => setAssignee("admin", a._id)}
              >
                <span className="text-gray-700">{a.naam}</span>
                {a._id === currentUserId ? (
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">
                    jij
                  </span>
                ) : null}
              </Option>
            ))}
          </Section>

          <Section title="Kwalificatie">
            {QUAL_OPTIONS.map((opt) => (
              <Option
                key={opt.value}
                active={filters.qualificationStatus === opt.value}
                onClick={() => setFilter("qualificationStatus", opt.value)}
              >
                <span className="text-gray-700">{opt.label}</span>
              </Option>
            ))}
          </Section>

          {labels.length > 0 ? (
            <Section title="Label">
              <Option
                active={!filters.labelId}
                onClick={() => setFilter("labelId", "")}
              >
                <span className="text-gray-700">Geen label-filter</span>
              </Option>
              {labels.map((l) => {
                const c = getLabelColors(l.color);
                return (
                  <Option
                    key={l._id}
                    active={filters.labelId === l._id}
                    onClick={() => setFilter("labelId", l._id)}
                  >
                    <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                    <span>{l.name}</span>
                  </Option>
                );
              })}
            </Section>
          ) : null}

          <Section title="Deadline">
            <Option
              active={!filters.deadline}
              onClick={() => setFilter("deadline", "")}
            >
              <span className="text-gray-700">Alle</span>
            </Option>
            <Option
              active={filters.deadline === "week"}
              onClick={() => setFilter("deadline", "week")}
            >
              <span className="text-gray-700">Deadline deze week</span>
            </Option>
            <Option
              active={filters.deadline === "overdue"}
              onClick={() => setFilter("deadline", "overdue")}
            >
              <span className="text-gray-700">Overdue</span>
            </Option>
          </Section>

          <Section title="Speciaal">
            <Toggle
              checked={filters.stuck}
              onChange={(v) => setFilter("stuck", v)}
              label="Vastgelopen"
              description="Meer dan 7 dagen zonder activiteit en niet eindstation"
            />
            <Toggle
              checked={filters.awaitingPartnerPayment}
              onChange={(v) => setFilter("awaitingPartnerPayment", v)}
              label="Wacht op betaling installateur"
              description="Partner-aanvragen waar de klant de betaling nog niet heeft bevestigd"
            />
            <Toggle
              checked={filters.archived === "true"}
              onChange={(v) => setFilter("archived", v ? "true" : "")}
              label="Toon archief"
              description="Aanvragen die zijn weggezet"
            />
          </Section>
        </div>

        <footer className="flex flex-shrink-0 items-center justify-between border-t border-gray-100 px-5 py-3">
          <button
            type="button"
            onClick={resetFilters}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Wis alle filters
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700"
          >
            Klaar
          </button>
        </footer>
      </aside>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>,
    document.body,
  );
};

export default BtwFiltersPanel;

export const countActiveFilters = (filters, { ignoreSearch = true } = {}) => {
  let n = 0;
  if (!ignoreSearch && filters.search) n++;
  if (filters.workflowStatusId) n++;
  if (filters.mine || filters.unassigned || filters.assignedToAdminId) n++;
  if (filters.qualificationStatus) n++;
  if (filters.stuck) n++;
  if (filters.awaitingPartnerPayment) n++;
  if (filters.labelId) n++;
  if (filters.deadline) n++;
  if (filters.archived === "true") n++;
  return n;
};
